import { Route } from "@angular/router";
import { initialDataResolver } from "app/app.resolvers";
import { AuthGuard } from "app/core/auth/guards/auth.guard";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";
import { GrowthRoleGuard } from "./core/auth/guards/growth-role.guard";
import { SuperAdminGuard } from "./core/auth/guards/super-admin.guard";
import { dtsEnabledGuard } from "./modules/admin/campaign/details/tabs/dts/dts-enabled.guard";
import { TicketConversationDetailComponent } from "./modules/admin/tickets/components/ticket-conversation-detail/ticket-conversation-detail.component";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/project'
    { path: "", pathMatch: "full", redirectTo: "campaigns" },

    // Redirect signed-in user to the '/dashboards/project'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: "signed-in-redirect", pathMatch: "full", redirectTo: "campaigns" },

    // Public read-only viewer for shared reports. Top-level + unguarded so
    // anyone with the link can open it — and ABOVE the AuthGuard'd blocks
    // because the router evaluates routes in order; otherwise the Freemium
    // block's AuthGuard would fire first and redirect to /sign-in before
    // Angular reaches the Landing block that owns this path.
    {
        path: "r/report",
        loadComponent: () =>
            import(
                "app/modules/admin/analytics/components/reportsanalytics-v2/report-shell/report-shell.component"
            ).then((m) => m.ReportsanalyticsV2ReportShellComponent),
    },

    // Freemium routes
    {
        path: "",
        canActivate: [AuthGuard, GrowthRoleGuard],
        canActivateChild: [AuthGuard, GrowthRoleGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: "advertisers",
                loadChildren: () => import("app/modules/admin/advertisers/advertisers.routes"),
            },
            {
                path: "agencies",
                loadChildren: () => import("app/modules/admin/agency/agency.routes"),
            },
            {
                path: "campaigns",
                loadChildren: () => import("app/modules/admin/campaign/campaigns.routes"),
            },
            {
                path: "analitycs",
                loadChildren: () => import("app/modules/admin/analytics/analytics.routes"),
            },
            {
                // Página propia del Drive to Store (v2). El componente sigue viviendo bajo
                // `campaign/details/tabs/dts/` porque comparte `core/` y `ui/` con la pestaña de la
                // v1.5, que es exactamente lo que la tarea pedía reutilizar.
                path: "drive-to-store",
                canActivate: [dtsEnabledGuard],
                loadComponent: () =>
                    import("app/modules/admin/campaign/details/tabs/dts/dts-page.component").then(
                        (m) => m.DtsPageComponent
                    ),
            },
            {
                // Glosario del DTS. Ruta hermana y no hija de `drive-to-store` porque esa página no
                // tiene `router-outlet`: es una pantalla completa, no un layout.
                path: "drive-to-store/glossary",
                canActivate: [dtsEnabledGuard],
                loadComponent: () =>
                    import("app/modules/admin/campaign/details/tabs/dts/dts-glossary-page.component").then(
                        (m) => m.DtsGlossaryPageComponent
                    ),
            },
            {
                path: "resources",
                loadChildren: () => import("app/modules/admin/resources/resources.routes"),
            },
            {
                path: "creatives",
                loadChildren: () => import("app/modules/admin/creative-gallery/creative-gallery.routes"),
            },
            {
                path: "creative-builder",
                loadChildren: () => import("app/modules/admin/creatives/creatives.routes"),
            },
            {
                path: "deals",
                loadChildren: () => import("app/modules/admin/deals/deals.routes"),
            },
            {
                path: "audiences",
                loadChildren: () => import("app/modules/admin/audience/audiences.routes"),
            },
            {
                path: "geo-audience",
                loadChildren: () => import("app/modules/admin/geo-audience/geo-audience.routes"),
            },
            {
                path: "tracking-events",
                loadChildren: () => import("app/modules/admin/tracking-events/tracking-events.routes"),
            },
            {
                path: "changelog",
                loadChildren: () => import("app/modules/admin/changelog/changelog.routes"),
            },
            {
                path: "organizations",
                loadChildren: () => import("app/modules/admin/organizations/organizations.routes"),
            },
            {
                path: "publishersList",
                loadChildren: () => import("app/modules/admin/campaign/details/tabs/publishers-list/publishers-list.routes"),
            },
            {
                path: "geolist",
                loadChildren: () => import("app/modules/admin/geolist/geolist.routes"),
            },
            {
                path: "device-trips",
                loadChildren: () => import("app/modules/admin/device-trips/device-trips.routes"),
            },
            {
                path: "tickets",
                loadChildren: () => import("app/modules/admin/tickets/tickets.routes"),
            },
            {
                path: "ticket-conversation-detail",
                component: TicketConversationDetailComponent,
            },
            {
                path: "profile",
                loadComponent: () => import("app/modules/admin/profile/profile.component").then((m) => m.ProfileComponent),
            },
            {
                path: "profile-settings",
                loadComponent: () =>
                    import("app/layout/common/user/profile-settings/profile-settings.component").then((m) => m.ProfileSettingsComponent),
            },
            {
                path: "mediasmart-credentials",
                canActivate: [SuperAdminGuard],
                loadComponent: () =>
                    import("app/modules/admin/mediasmart-credentials/mediasmart-credentials-page.component").then(
                        (m) => m.MediaSmartCredentialsPageComponent,
                    ),
            },
        ],
    },

    // Auth routes for guests
    {
        path: "",
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: "empty",
        },
        children: [
            {
                path: "confirmation-required",
                loadChildren: () => import("app/modules/auth/confirmation-required/confirmation-required.routes"),
            },
            {
                path: "forgot-password",
                loadChildren: () => import("app/modules/auth/forgot-password/forgot-password.routes"),
            },
            {
                path: "reset-password",
                loadChildren: () => import("app/modules/auth/reset-password/reset-password.routes"),
            },
            {
                path: "sign-in",
                loadChildren: () => import("app/modules/auth/sign-in/sign-in.routes"),
            },
            {
                path: "sign-up",
                loadChildren: () => import("app/modules/auth/sign-up/sign-up.routes"),
            },
            {
                path: "verify-code",
                loadChildren: () => import("app/modules/auth/verify-code/verify-code.routes"),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: "",
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: "empty",
        },
        children: [
            {
                path: "sign-out",
                loadChildren: () => import("app/modules/auth/sign-out/sign-out.routes"),
            },
            {
                path: "unlock-session",
                loadChildren: () => import("app/modules/auth/unlock-session/unlock-session.routes"),
            },
        ],
    },

    // Account switcher (no guard — accessible when active session expired but others exist)
    {
        path: "switch-account",
        component: LayoutComponent,
        data: {
            layout: "empty",
        },
        children: [
            {
                path: "",
                loadComponent: () =>
                    import("app/modules/auth/switch-account/switch-account.component").then(
                        (m) => m.SwitchAccountComponent
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: "",
        component: LayoutComponent,
        data: {
            layout: "empty",
        },
        children: [
            {
                path: "home",
                loadChildren: () => import("app/modules/landing/home/home.routes"),
            },
            {
                path: "",
                loadChildren: () => import("app/modules/public/public.routes"),
            },
        ],
    },

    // UI routes
    {
        path: "",
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard, SuperAdminGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [],
    },

    // Admin routes
    {
        path: "",
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            // UI
            {
                path: "ui",
                canActivate: [SuperAdminGuard],
                children: [
                    {
                        path: "",
                        loadChildren: () => import("app/modules/ui/ui.routes"),
                    },
                ],
            },
            //Doc System Ui
            {
                path: "doc-system",
                canActivate: [SuperAdminGuard],
                children: [
                    {
                        path: "doc-ui",
                        loadChildren: () => import("app/modules/admin/doc-ui/doc-ui.routes").then((m) => m.DocUiRoutingModule),
                    },
                ],
            },
            // Dashboards
            {
                path: "dashboards",
                children: [
                    {
                        path: "project",
                        loadChildren: () => import("app/modules/admin/dashboards/project/project.routes"),
                    },
                ],
            },
            // Pages
            {
                path: "pages",
                children: [
                    // Activities

                    // Error
                    {
                        path: "error",
                        children: [
                            {
                                path: "404",
                                loadChildren: () => import("app/modules/admin/pages/error/error-404/error-404.routes"),
                            },
                            {
                                path: "500",
                                loadChildren: () => import("app/modules/admin/pages/error/error-500/error-500.routes"),
                            },
                            {
                                path: "503",
                                loadChildren: () => import("app/modules/admin/pages/error/error-503/error-503.routes"),
                            },
                        ],
                    },
                ],
            },
            {
                path: "error/404",
                loadChildren: () => import("app/modules/admin/pages/error/error-404/error-404.routes"),
            },
            {
                path: "error/500",
                loadChildren: () => import("app/modules/admin/pages/error/error-500/error-500.routes"),
            },
            {
                path: "error/503",
                loadChildren: () => import("app/modules/admin/pages/error/error-503/error-503.routes"),
            },
            // 404 & Catch all
            {
                path: "404-not-found",
                pathMatch: "full",
                loadChildren: () => import("app/modules/admin/pages/error/error-404/error-404.routes"),
            },
            { path: "**", redirectTo: "404-not-found" },
        ],
    },
];
